import { FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import type { KbCategory, KbTag, KbConfidentiality } from "@/api/knowledgeBase";

// Knowledge Base v1.0 PR 4 — shared metadata form used by both the upload
// dialog and the edit dialog. Pure controlled component; the parent owns
// state + persistence.

export interface KbDocumentMetadataState {
  title: string;
  description: string;
  primaryCategoryCode: string;
  registryAbbrev: string;
  documentVersion: string;
  effectiveDate: string;
  confidentiality: KbConfidentiality;
  tagCodes: string[];
}

export const EMPTY_METADATA: KbDocumentMetadataState = {
  title: "",
  description: "",
  primaryCategoryCode: "",
  registryAbbrev: "",
  documentVersion: "",
  effectiveDate: "",
  confidentiality: "InternalOnly",
  tagCodes: [],
};

interface KbDocumentMetadataFormProps {
  value: KbDocumentMetadataState;
  onChange: (next: KbDocumentMetadataState) => void;
  categories: KbCategory[];
  tags: KbTag[];
  errors: string[];
}

export function KbDocumentMetadataForm({
  value,
  onChange,
  categories,
  tags,
  errors,
}: KbDocumentMetadataFormProps) {
  const set = (patch: Partial<KbDocumentMetadataState>) =>
    onChange({ ...value, ...patch });

  const toggleTag = (code: string) => {
    if (value.tagCodes.includes(code)) {
      set({ tagCodes: value.tagCodes.filter((c) => c !== code) });
    } else {
      set({ tagCodes: [...value.tagCodes, code] });
    }
  };

  return (
    <div className="space-y-space-3">
      <FormField label="Title" required htmlFor="kb-title">
        <Input
          id="kb-title"
          value={value.title}
          onChange={(e) => set({ title: e.target.value })}
          maxLength={200}
          autoFocus
        />
      </FormField>

      <FormField label="Description" htmlFor="kb-description">
        <Textarea
          id="kb-description"
          value={value.description}
          onChange={(e) => set({ description: e.target.value })}
          rows={3}
          maxLength={1000}
        />
      </FormField>

      <FormField label="Primary category" required htmlFor="kb-category">
        <Select
          id="kb-category"
          value={value.primaryCategoryCode}
          onChange={(e) => set({ primaryCategoryCode: e.target.value })}
        >
          <option value="">Choose a category…</option>
          {categories.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </Select>
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
        <FormField label="Registry abbrev." htmlFor="kb-reg">
          <Input
            id="kb-reg"
            value={value.registryAbbrev}
            onChange={(e) => set({ registryAbbrev: e.target.value })}
            maxLength={50}
          />
        </FormField>
        <FormField label="Version" htmlFor="kb-version">
          <Input
            id="kb-version"
            value={value.documentVersion}
            onChange={(e) => set({ documentVersion: e.target.value })}
            maxLength={50}
          />
        </FormField>
        <FormField label="Effective date" htmlFor="kb-effective">
          <Input
            id="kb-effective"
            type="date"
            value={value.effectiveDate}
            onChange={(e) => set({ effectiveDate: e.target.value })}
          />
        </FormField>
      </div>

      <FormField label="Confidentiality" required htmlFor="kb-conf">
        <Select
          id="kb-conf"
          value={value.confidentiality}
          onChange={(e) =>
            set({ confidentiality: e.target.value as KbConfidentiality })
          }
        >
          <option value="InternalOnly">Internal only</option>
          <option value="CounterpartyFacing">Counterparty-facing</option>
        </Select>
      </FormField>

      <FormField label="Tags" htmlFor="kb-tags">
        <div className="flex flex-wrap gap-space-1" id="kb-tags">
          {tags.length === 0 ? (
            <span className="text-caption text-gray-mid italic">
              No tags defined yet.
            </span>
          ) : (
            tags.map((t) => {
              const selected = value.tagCodes.includes(t.code);
              return (
                <button
                  key={t.code}
                  type="button"
                  onClick={() => toggleTag(t.code)}
                  aria-pressed={selected}
                  className={
                    selected
                      ? "px-space-2 py-[4px] rounded-full text-caption font-display font-semibold border bg-gold/15 border-gold text-gold-shadow"
                      : "px-space-2 py-[4px] rounded-full text-caption font-display font-semibold border bg-white-pure border-border-light text-gray-mid hover:border-gold-shadow"
                  }
                >
                  {t.label}
                </button>
              );
            })
          )}
        </div>
      </FormField>

      {errors.length > 0 && (
        <ul
          className="list-disc pl-space-4 text-caption text-status-error"
          role="alert"
        >
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
